/**
 * ErrorBoundary — render-crash containment for the Studio shell.
 *
 * Why: React unmounts the ENTIRE tree when any component throws during render
 * and nothing catches it. On 2026-07-27 one orphaned identifier in a journey
 * preview pane (`brand` — see changelog/2026-07-27T1730) blanked the whole app
 * to grey: no rail, no topbar, no way back. A boundary turns that class of
 * failure into a contained, honest panel with a way out.
 *
 * Three variants, deliberately different in weight:
 *   app    — last resort around the whole shell. Full-page; offers a reload.
 *   screen — one per route (AppRouter). The rail/topbar survive, so the user
 *            can navigate away; auto-clears when the route changes.
 *   pane   — an embedded region (live preview, editor canvas, chart). Inline,
 *            sized to its container; the rest of the screen keeps working.
 *
 * This reports; it does not repair. The panel names the failing surface and
 * shows the real error text — never a cheerful euphemism that hides a defect.
 */
import React from "react";
import { Btn } from "@v0y4ge/jrni-ui";
import { useT } from "../lib/i18n";

type Variant = "app" | "screen" | "pane";

/* ── CRASH PROBE ──────────────────────────────────────────────────────────
 *
 * WHY THIS EXISTS (2026-09-21): until now the only way to see a crash panel
 * was to break real code and undo it, so the four states below — three of
 * which are invisible to a single throw — had never all been seen at once.
 *
 * WHY IT LIVES INSIDE THE BOUNDARY: `getDerivedStateFromError` only catches
 * throws from CHILDREN, so a probe has to render as a child. Putting it here
 * means every existing `scope` string in the app is already a test handle —
 * no call site changes, and the `app` and `screen` variants become reachable
 * on purpose instead of only by accident.
 *
 *   window.__crashDebug().scopes                   the handles you can arm now
 *   window.__crash("ops.today")                    one-shot: Try again clears it
 *   window.__crash("ops.today", {mode:"persist"})  stays armed → failedAfterRetry
 *   window.__crash("ops.today", {kind:"zod"})      JSON dump → exercises summarise()
 *   window.__crashClear()                          disarm AND reset caught panels
 *
 * EXACT SCOPES ONLY, AND ARMS EXPIRE IN TEN SECONDS. Both rules are scar
 * tissue from building this: a wildcard also armed the app-level boundary,
 * whose full-page panel unmounts the shell and every boundary in it, and a
 * long-lived arm fired on a later load where it read as a genuine crash.
 *
 * NO URL PARAM. An arm that survives a reload cannot be told apart from a real
 * crash, which is the one thing a debugging aid must never do.
 *
 * INERT UNTIL ARMED. No timer, no listener work, no render cost until someone
 * calls it from a console — which is why it ships rather than hiding behind a
 * build flag that would leave it untested in the environment that needs it.
 *
 * WHAT A GREEN RUN DOES NOT PROVE: React boundaries do not catch event
 * handlers, async rejections, or a throw in the boundary's own render. Those
 * need a `window.onerror` / `unhandledrejection` surface, which this is not.
 */
type CrashKind = "error" | "zod";
interface CrashArm { readonly scope: string; readonly mode: "once" | "persist"; readonly kind: CrashKind; readonly at: number }

/* The registry lives on `window`, NOT in module scope, and that is deliberate.
 * This harness can evaluate the same module twice under two specifiers (the
 * barrel faults of 2026-09-18 were the same shape). With a module-local array,
 * `__crash` installed by instance A pushes onto A's registry while the mounted
 * boundaries belong to B — arming appears to succeed and nothing ever fires.
 * A shared window slot is immune to that, at the cost of one global. */
interface CrashRegistry { armed: CrashArm[]; listeners: Set<{ getScope: () => string; notify: () => void; reset: () => void }> }
function registry(): CrashRegistry {
  const w = window as unknown as { __crashRegistry?: CrashRegistry };
  if (!w.__crashRegistry) w.__crashRegistry = { armed: [], listeners: new Set() };
  return w.__crashRegistry;
}

/* An arm is good for ten seconds and no longer.
 *
 * WHY: an arm that outlives the moment you fired it is indistinguishable from a
 * real crash. During this probe's own bring-up an arm reached the app-level
 * boundary on what should have been a clean load — the whole shell replaced by
 * a crash panel, with a probe as the invisible cause. A short fuse means the
 * worst case is a stale arm that has already expired by the time anyone looks. */
const ARM_TTL_MS = 10_000;

function armFor(scope: string): CrashArm | null {
  if (typeof window === "undefined") return null;
  const reg = registry();
  const now = Date.now();
  for (let i = reg.armed.length - 1; i >= 0; i--) {
    if (now - reg.armed[i].at > ARM_TTL_MS) reg.armed.splice(i, 1);
  }
  // EXACT scope only. A wildcard would also arm the app-level boundary, whose
  // full-page panel unmounts the shell (and every other boundary with it), so
  // the one crash you asked for takes out the surface you were testing.
  return reg.armed.find((a) => a.scope === scope) ?? null;
}

/* A real Zod parse failure's `message` is a JSON array of issues, which is the
 * one input `summarise()` has a dedicated branch for. Shaped like a genuine
 * session-document failure so the summarised line reads like a real one. */
const ZOD_STYLE_MESSAGE = JSON.stringify([
  { code: "invalid_type", expected: "string", received: "undefined", path: ["blocks", 2, "data", "title"], message: "Required" },
  { code: "invalid_type", expected: "array", received: "null", path: ["blocks", 2, "data", "items"], message: "Expected array, received null" },
]);

/* PURE ON PURPOSE — do not disarm from here.
 *
 * React 18 retries a throwing render from the root before handing the error to
 * a boundary. The first cut of this disarmed the one-shot inside render: the
 * retry then found nothing armed, rendered cleanly, and React discarded the
 * error as recovered — arm consumed, no panel, no `componentDidCatch`. The
 * probe looked broken and the boundary looked broken, and neither was.
 * Consuming the arm belongs in the commit phase; see `componentDidCatch`. */
function CrashProbe({ arm }: { arm: CrashArm }): never {
  throw new Error(
    arm.kind === "zod" ? ZOD_STYLE_MESSAGE : `Crash probe — deliberate throw in "${arm.scope}"`,
  );
}

declare global {
  interface Window {
    __crash?: (scope: string, opts?: { mode?: "once" | "persist"; kind?: CrashKind }) => string;
    __crashClear?: () => string;
    /** Armed arms, plus the scopes mounted RIGHT NOW — the list of handles you
     *  can actually arm. An empty list means no boundary is mounted, which is a
     *  different fault from a probe that fails to throw. */
    __crashDebug?: () => { armed: CrashArm[]; scopes: string[] };
  }
}

if (typeof window !== "undefined" && !window.__crash) {
  window.__crash = (scope, opts = {}) => {
    const reg = registry();
    const live = [...reg.listeners].map((l) => l.getScope());
    if (!scope || scope === "*") {
      return `__crash needs an exact scope. Mounted now: ${live.join(", ") || "(none)"}`;
    }
    if (!live.includes(scope)) {
      return `no mounted boundary has scope "${scope}". Mounted now: ${live.join(", ") || "(none)"}`;
    }
    const arm: CrashArm = { scope, mode: opts.mode ?? "once", kind: opts.kind ?? "error", at: Date.now() };
    reg.armed.push(arm);
    reg.listeners.forEach((l) => l.notify());
    return `armed "${scope}" (${arm.mode}, ${arm.kind}); expires in ${ARM_TTL_MS / 1000}s`;
  };
  window.__crashClear = () => {
    const reg = registry();
    reg.armed.length = 0;
    // Disarming is not enough: a boundary that already caught keeps its error
    // and goes on showing the old panel, so the NEXT arm appears to do nothing.
    // Clear means "back to normal", so reset the caught state too.
    reg.listeners.forEach((l) => l.reset());
    return "disarmed and reset";
  };
  window.__crashDebug = () => {
    const reg = registry();
    return { armed: [...reg.armed], scopes: [...reg.listeners].map((l) => l.getScope()) };
  };
}

interface Props {
  children: React.ReactNode;
  /** Stable id of the guarded surface — route id, or "<screen>:<pane>". */
  scope: string;
  /** Human label for the panel ("Journey preview"). Defaults to `scope`. */
  label?: string;
  variant?: Variant;
  /** Changing this value clears a caught error (AppRouter passes the route). */
  resetKey?: string;
  /**
   * Renders the fallback inside a chrome wrapper. Screens own their own rail +
   * topbar (they render `<Screen>` themselves), so without this a screen crash
   * would take the navigation with it and strand the user — AppRouter passes a
   * bare `<Screen>` so the rail stays usable.
   */
  wrap?: (fallback: React.ReactNode) => React.ReactNode;
}
interface State { error: Error | null; stack: string; retried: boolean; failedAfterRetry: boolean }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, stack: "", retried: false, failedAfterRetry: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Commit phase, so this is where a one-shot arm is consumed: "Try again"
    // then finds nothing armed and genuinely recovers, while `persist` stays
    // armed and drives `failedAfterRetry` on the next attempt.
    const arm = armFor(this.props.scope);
    if (arm && arm.mode === "once") {
      const list = registry().armed;
      const i = list.indexOf(arm);
      if (i >= 0) list.splice(i, 1);
    }
    // `retried` is only true when the user pressed Try again, so this flags a
    // genuine repeat — not React's double-invoke in development.
    this.setState((s) => ({ stack: info.componentStack || "", failedAfterRetry: s.retried }));
    // Keep the real error in the console for anyone debugging the preview.
    console.error(`[render crash] ${this.props.scope}:`, error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, stack: "", retried: false, failedAfterRetry: false });
    }
  }

  /* Arming from a console has to reach boundaries that are already mounted and
   * otherwise have no reason to re-render.
   *
   * `getScope` reads props LIVE rather than caching the scope at mount, because
   * AppRouter renders `<ErrorBoundary scope={nav.route}>` with no `key`: React
   * keeps ONE instance across route changes and just hands it new props. A
   * cached label made the registry advertise the route you arrived on — arming
   * it passed the mounted-scope check, then matched nothing in render, and the
   * probe looked intermittently broken. */
  private onArm = {
    getScope: () => this.props.scope,
    notify: () => this.forceUpdate(),
    reset: () => this.setState({ error: null, stack: "", retried: false, failedAfterRetry: false }),
  };
  componentDidMount() { registry().listeners.add(this.onArm); }
  componentWillUnmount() { registry().listeners.delete(this.onArm); }

  retry = () => this.setState({ error: null, stack: "", retried: true });

  render() {
    if (!this.state.error) {
      const arm = armFor(this.props.scope);
      // Probe renders as a SIBLING of children: the real subtree still mounts,
      // so the panel that appears is the one a genuine crash in a working
      // surface would produce, not the one an empty surface would.
      if (arm) return <>{this.props.children}<CrashProbe arm={arm} /></>;
      return this.props.children;
    }
    const panel = (
      <CrashPanel
        variant={this.props.variant ?? "screen"}
        label={this.props.label ?? this.props.scope}
        scope={this.props.scope}
        error={this.state.error}
        stack={this.state.stack}
        repeated={this.state.failedAfterRetry}
        onRetry={this.retry}
      />
    );
    return this.props.wrap ? this.props.wrap(panel) : panel;
  }
}

/** Convenience wrapper for an embedded region. */
export function PaneBoundary({ scope, label, resetKey, children }: Omit<Props, "variant">) {
  return <ErrorBoundary variant="pane" scope={scope} label={label} resetKey={resetKey}>{children}</ErrorBoundary>;
}

/** One readable line out of a message that may be a JSON validator dump. */
function summarise(message: string): string {
  const trimmed = message.trim();
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      const issues = Array.isArray(parsed) ? parsed : [parsed];
      const first = issues[0] || {};
      if (first.message || first.path) {
        const where = Array.isArray(first.path) ? first.path.join(".") : String(first.path ?? "");
        const head = `${where || "response"}: ${first.message || first.code || "invalid"}`;
        return issues.length > 1 ? `${head} (+${issues.length - 1} more)` : head;
      }
    } catch { /* not JSON after all — fall through to the clamp */ }
  }
  return trimmed.length > 300 ? `${trimmed.slice(0, 300)}…` : trimmed;
}

function CrashPanel({ variant, label, scope, error, stack, repeated, onRetry }: {
  variant: Variant; label: string; scope: string; error: Error; stack: string; repeated: boolean; onRetry: () => void;
}) {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const message = (error && error.message) || String(error);
  /* A Zod parse failure's `message` is a JSON array of issues — printed raw it
     buries the panel in validator output. Summarise it to one line (the full
     text still goes to Copy error details). */
  const shown = summarise(message);
  const details = `${scope}\n${error?.name || "Error"}: ${message}\n${error?.stack || ""}\n--- component stack ---${stack}`;
  const copy = () => {
    try {
      void navigator.clipboard.writeText(details);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable in some preview hosts */ }
  };

  const pane = variant === "pane";
  const wrap: React.CSSProperties = variant === "app"
    ? { minHeight: "100vh", display: "grid", placeItems: "center", padding: 40, background: "var(--jrni-color-surface-canvas)" }
    : pane
      ? { display: "flex", alignItems: "center", justifyContent: "center", padding: 20, minHeight: 180, height: "100%" }
      : { minHeight: "60vh", display: "grid", placeItems: "center", padding: 40 };

  return (
    <div style={{ ...wrap, fontFamily: "var(--jrni-font-family-sans)" }} role="alert" data-crash-scope={scope}>
      <div style={{
        maxWidth: pane ? 380 : 560, width: "100%", background: "var(--jrni-color-surface-raised, #fff)",
        border: "1px solid var(--jrni-color-neutral-5)", borderRadius: 12, padding: pane ? "18px 20px" : "26px 28px",
        boxShadow: pane ? "none" : "0 10px 30px -22px rgba(20,16,46,0.5)",
      }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span aria-hidden="true" style={{
            width: 22, height: 22, borderRadius: 999, flexShrink: 0, display: "grid", placeItems: "center",
            background: "var(--jrni-color-semantic-red-4, #FDECEF)", color: "var(--jrni-color-semantic-red-1, #C4123F)",
            fontSize: 13, fontWeight: 800, marginTop: 1,
          }}>!</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: pane ? 14 : 16.5, fontWeight: 700, color: "var(--jrni-color-neutral-1)", marginBottom: 4 }}>
              {variant === "app"
                ? t("shell.errorBoundary.titleApp", "Studio hit an error")
                : pane
                  ? t("shell.errorBoundary.titlePane", "This panel didn't load")
                  : t("shell.errorBoundary.titleScreen", "This screen didn't load")}
            </div>
            <div style={{ fontFamily: "var(--jrni-font-family-mono, ui-monospace, monospace)", fontSize: 11, color: "var(--jrni-color-text-soft, #6B7280)", marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 12.5, color: "var(--jrni-color-neutral-2)", lineHeight: 1.55, textWrap: "pretty" }}>
              {variant === "app"
                ? t("shell.errorBoundary.bodyApp", "The app hit an error it couldn't recover from. Reloading usually clears it — if it happens again, send the details below.")
                : pane
                  ? t("shell.errorBoundary.bodyPane", "This panel failed to render. The rest of the screen still works.")
                  : t("shell.errorBoundary.bodyScreen", "This screen failed to render. Everything else still works — you can switch screens from the menu.")}
            </div>
            <div style={{
              marginTop: 10, padding: "8px 10px", borderRadius: 8, background: "var(--jrni-color-neutral-6, #F4F5F7)",
              fontFamily: "var(--jrni-font-family-mono, ui-monospace, monospace)", fontSize: 11.5,
              color: "var(--jrni-color-neutral-2)", wordBreak: "break-word", lineHeight: 1.45,
            }}>{shown}</div>
            {repeated ? (
              <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--jrni-color-semantic-orange-1, #9A5B00)" }}>
                {t("shell.errorBoundary.repeated", "It failed again after retrying — this needs a code fix, not another attempt.")}
              </div>
            ) : null}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
              {variant === "app" ? (
                <Btn size="sm" onClick={() => window.location.reload()}>
                  {t("shell.errorBoundary.reload", "Reload the app")}
                </Btn>
              ) : (
                <Btn size="sm" onClick={onRetry}>
                  {t("shell.errorBoundary.retry", "Try again")}
                </Btn>
              )}
              <Btn variant="secondary" size="sm" onClick={copy}>
                {copied ? t("shell.errorBoundary.copied", "Copied") : t("shell.errorBoundary.copyDetails", "Copy error details")}
              </Btn>
              <Btn variant="secondary" size="sm" onClick={() => setOpen((o) => !o)}>
                {open ? t("shell.errorBoundary.hideTechnical", "Hide technical detail") : t("shell.errorBoundary.showTechnical", "Technical detail")}
              </Btn>
            </div>
            {open ? (
              <pre style={{
                marginTop: 12, marginBottom: 0, maxHeight: 220, overflow: "auto", padding: 10, borderRadius: 8,
                background: "var(--jrni-color-neutral-6, #F4F5F7)", border: "1px solid var(--jrni-color-neutral-5)",
                fontFamily: "var(--jrni-font-family-mono, ui-monospace, monospace)", fontSize: 11,
                color: "var(--jrni-color-neutral-2)", whiteSpace: "pre-wrap", lineHeight: 1.5,
              }}>{details}</pre>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* The local button-style helper was deleted in the Button pass (2026-08-18): the four
 * actions above are now jrni-ui <Btn>s (primary + secondary). */
